import axios from 'axios';
import { normaliseLookupInput } from './lookupInput';
import axiosInstance from './axiosConfig';
import { getDomain } from './constants';
import { convertDateFormat, formatDateToYYYYMMDD, getCookie, localStorageUpdateAfterLogin, RetryConfig, SignUpFormType, UserEligibilityType, withRetry } from './utils';


// API's

export const loginApi = async (username: string, password: string) => {
    const response = await axiosInstance.post('/auth/login', {
      username,
      password,
    });
    return response.data;
  };

// Forgot-password (Cognito code flow): initiate emails a 6-digit code; confirm sets the new password.
export const initiateForgotPasswordApi = async (username: string) => {
  try {
    const response = await axiosInstance.put(`/auth/initiateForgotPassword?username=${encodeURIComponent(username)}`);
    return response.data;
  } catch (error) {
    // Log only the status — the axios error carries config.url, which here holds the username.
    console.error("Error initiating forgot password. Status:", (error as any)?.response?.status);
    throw error;
  }
};

export const forgotPasswordApi = async (payload: { username: string; confirmationCode: string; newPassword: string }) => {
  try {
    const response = await axiosInstance.put('/auth/forgotPassword', payload);
    return response.data;
  } catch (error) {
    // Log only the status — the axios error carries config.data (username, code, new password).
    console.error("Error resetting password. Status:", (error as any)?.response?.status);
    throw error;
  }
};

// Reset-password (token+link flow): the emailed link carries a one-time token; confirm sets the new password.
export const resetPasswordApi = async (payload: { token: string; newPassword: string }) => {
  try {
    const response = await axiosInstance.post('/auth/reset-password', payload);
    return response.data;
  } catch (error) {
    // Log only the status — the axios error carries config.data (the one-time token + new password).
    console.error("Error resetting password. Status:", (error as any)?.response?.status);
    throw error;
  }
};

  export const isValidSignUpFormData = (form: SignUpFormType, theme?: any) => {
  if (!form.firstName || !(form.firstName?.length > 2)) {
    return 'Please enter first name';
  }
  if (!form.lastName || !(form.lastName?.length > 2)) {
    return 'Please enter last name';
  }
  if (!form.userName || !(form.userName?.length > 2)) {
    return 'Please enter username';
  }
  if (!form.email || !form.email.includes('@')) {
    return 'Please enter valid email';
  }
  if (!form.password || !(form.password?.length > 8)) {
    return 'Please enter a password with at least 8 characters';
  }
  if (form.password !== form.confirmPassword) {
    return 'Passwords do not match';
  }
  if (!form.phoneNumber || !form.phoneNumber?.match(/^[0-9]{10}$/)) {
    return 'Please enter a valid phone number';
  }

  // Only validate optional fields if theme is loaded and the field is enabled
  if (theme && theme.showDobField && (!form.dateOfBirth || form.dateOfBirth.trim() === '')) {
    return 'Please enter date of birth';
  }

  if (theme && theme.showSsnField && (!form.uniqueCustomerIdentifier || !form.uniqueCustomerIdentifier.match(/^[0-9]{4}$/))) {
    return 'Please enter last 4 digits of SSN';
  }

  if (theme && theme.showGarantorNumberField && (!form.guarantorNumber || form.guarantorNumber.trim() === '')) {
    return 'Please enter guarantor number';
  }

  if (theme && theme.showGarantorLastNameField && (!form.guarantorLastName || form.guarantorLastName.trim() === '')) {
    return 'Please enter guarantor last name';
  }

  return null;
};

// name@example.com — a real address shape, not just an "@" presence check.
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Per-field sign-up validation. Returns a map of { field -> message } for every field that is
 * currently invalid (empty map === form is valid). Unlike isValidSignUpFormData (which returns only
 * the first error string), this drives inline, field-level error messages so the user can see exactly
 * what to fix instead of staring at a silently-disabled button.
 */
export const getSignupFieldErrors = (
  form: SignUpFormType,
  theme?: any,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const nameRe = /^[a-zA-Z][a-zA-Z\s.'-]*$/; // letters, spaces, apostrophe, hyphen — no digits

  const firstName = form.firstName?.trim() ?? '';
  if (!firstName) errors.firstName = 'First name is required.';
  else if (firstName.length < 2) errors.firstName = 'Enter at least 2 characters.';
  else if (!nameRe.test(firstName)) errors.firstName = 'Letters only — no numbers or symbols.';

  const lastName = form.lastName?.trim() ?? '';
  if (!lastName) errors.lastName = 'Last name is required.';
  else if (lastName.length < 2) errors.lastName = 'Enter at least 2 characters.';
  else if (!nameRe.test(lastName)) errors.lastName = 'Letters only — no numbers or symbols.';

  const userName = form.userName?.trim() ?? '';
  if (!userName) errors.userName = 'Username is required.';
  else if (userName.length < 3) errors.userName = 'Enter at least 3 characters.';

  const email = form.email?.trim() ?? '';
  if (!email) errors.email = 'Email is required.';
  else if (!EMAIL_REGEX.test(email)) errors.email = 'Enter a valid email, e.g. name@example.com.';

  // US numbers: 10 digits, tolerating formatting and an optional +1 country code.
  const phoneDigits = (form.phoneNumber ?? '').replace(/\D/g, '');
  const usPhone =
    phoneDigits.length === 11 && phoneDigits.startsWith('1') ? phoneDigits.slice(1) : phoneDigits;
  if (!phoneDigits) errors.phoneNumber = 'Phone number is required.';
  else if (usPhone.length !== 10) errors.phoneNumber = 'Enter a valid US phone number.';

  // Mirror the Cognito pool policy so we catch it here instead of via a 400 on submit.
  const pw = form.password ?? '';
  if (!pw) {
    errors.password = 'Password is required.';
  } else if (
    pw.length < 8 ||
    !/[A-Z]/.test(pw) ||
    !/[a-z]/.test(pw) ||
    !/[0-9]/.test(pw) ||
    !/[^A-Za-z0-9]/.test(pw)
  ) {
    errors.password =
      'At least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character.';
  }

  if (!form.confirmPassword) errors.confirmPassword = 'Please re-enter your password.';
  else if (pw !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match.';

  if (theme?.showDobField) {
    const dob = form.dateOfBirth?.trim() ?? '';
    if (!dob) {
      errors.dateOfBirth = 'Date of birth is required.';
    } else {
      const d = new Date(dob);
      if (isNaN(d.getTime())) errors.dateOfBirth = 'Enter a valid date.';
      else if (d.getFullYear() < 1900) errors.dateOfBirth = 'Enter a valid year.';
      else if (d > new Date()) errors.dateOfBirth = 'Date of birth cannot be in the future.';
    }
  }

  if (theme?.showSsnField) {
    const ssn = form.uniqueCustomerIdentifier?.trim() ?? '';
    if (!ssn) errors.uniqueCustomerIdentifier = 'Last 4 digits of SSN are required.';
    else if (!/^[0-9]{4}$/.test(ssn)) errors.uniqueCustomerIdentifier = 'Enter exactly 4 digits.';
  }

  if (theme?.showGarantorNumberField) {
    const gn = form.guarantorNumber?.trim() ?? '';
    if (!gn) errors.guarantorNumber = 'Guarantor number is required.';
    else if (!/\d/.test(gn))
      errors.guarantorNumber = 'Enter the guarantor number from your statement.';
  }

  if (theme?.showGarantorLastNameField) {
    const gln = form.guarantorLastName?.trim() ?? '';
    if (!gln) errors.guarantorLastName = "Guarantor's last name is required.";
    else if (gln.length < 2) errors.guarantorLastName = 'Enter at least 2 characters.';
  }

  return errors;
};

  export const signUpApi = async (form: SignUpFormType, theme?: any) => {
  const payload: any = {
    firstName: form.firstName,
    lastName: form.lastName,
    username: form.userName,
    email: form.email,
    password: form.password,
    phone: form.phoneNumber,
  };

  // Only add optional fields if they are enabled in the theme and have values
  if (theme?.showDobField && form.dateOfBirth) {
    payload.dateOfBirth = form.dateOfBirth;
  }

  // Normalised, exactly as the lookup path does. Signup previously sent the raw value, so a CMRN
  // pasted out of a statement PDF with a leading space or a zero-width character was persisted on the
  // User row verbatim -- and then every authenticated lookup used that stored value, with no profile
  // screen to correct it. Same normaliser as the verification modal so the two cannot disagree.
  if (theme?.showGarantorNumberField && form.guarantorNumber) {
    payload.guarantorNumber = normaliseLookupInput(form.guarantorNumber);
  }

  if (theme?.showGarantorLastNameField && form.guarantorLastName) {
    payload.guarantorLastName = normaliseLookupInput(form.guarantorLastName);
  }

  // For SSN field, we'll use uniqueCustomerIdentifier
  if (theme?.showSsnField && form.uniqueCustomerIdentifier) {
    payload.uniqueCustomerIdentifier = form.uniqueCustomerIdentifier;
  }

  const response = await axiosInstance.post('/auth/signup', payload);
  return response.data;
};
  
  export const postUserEligibilityData = async (form: UserEligibilityType) => {
    const response = await axiosInstance.post('/user_eligibility_data/create', {
      membershipId: form.membershipId,
      insuranceCompanyName: form.insuranceCompanyName,
      primaryInsuranceSubscriber: form.primaryInsuranceSubscriber,
      userDob: form.dateOfBirth,
    });
    return response.data;
  };
  
  export interface Insurance {
    id: number;
    name: string;
    description: string | null;
    active: boolean;
    createdAt: string;
    updatedAt: string;
  }

  export const getInsurances = async (activeOnly = true): Promise<Insurance[]> => {
    try {
      const response = await axiosInstance.get('/public/api/insurances', { params: { activeOnly } });
      return response.data;
    } catch (error) {
      console.error("Error fetching insurances:", error);
      throw error;
    }
  };

  export const getPayers = async () => {
    try {
      const response = await axiosInstance.get("/payers");
      return response.data;
    } catch (error) {
      console.error("Error fetching payers:", error);
      throw error;
    }
  };
  
  export const confirmSignUpOtp = async (username: string, otp: string) => {
    try {
      const response = await axiosInstance.post(
        // Encode both params — a username or code containing & / + would otherwise mangle the query.
        `/auth/confirm?username=${encodeURIComponent(username)}&confirmationCode=${encodeURIComponent(otp)}`,
        undefined,
        // The OTP screen renders its own inline error — skip the app-wide error toast so a failed
        // confirm doesn't visually collide with the local message / the success redirect.
        { skipGlobalError: true } as any
      );
      return response.data;
    } catch (error) {
      console.error("Error signing up:", error);
      throw error;
    }
  };

  // Re-send the Cognito sign-up confirmation code to the user's email. GET, no body —
  // the backend resend endpoint keys off the username alone.
  export const resendConfirmationCode = async (username: string) => {
    try {
      const response = await axiosInstance.get(
        `/auth/resendConfirmationCode?username=${encodeURIComponent(username)}`,
        // Inline "Resend" feedback is shown on the OTP screen — skip the app-wide error toast.
        { skipGlobalError: true } as any
      );
      return response.data;
    } catch (error) {
      // Log only the status — the axios error carries config.url, which here holds the username.
      console.error("Error resending confirmation code. Status:", (error as any)?.response?.status);
      throw error;
    }
  };


  export const getPendingTransactions = async (guarantorLastName?: string, guarantorNumber?: string, paymentId?: string) => {
    try {
      let url = '/public/api/payments/patient-accounts?';
      const params = new URLSearchParams();
      
      if (guarantorLastName) {
        params.append('guarantorLastName', guarantorLastName);
      }
      if (guarantorNumber) {
        params.append('guarantorNumber', guarantorNumber);
      }
      if (paymentId) {
        params.append('paymentId', paymentId);
      }
      
      url += params.toString();
      const response = await axiosInstance.get(url);
      console.log("Response from patient accounts:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching patient accounts:", error);
      throw error;
    }
  };

  export const updatePostPayment = async (amount : number = 0, referenceId: string, payabaliResponse: any, paymentIdentityId: string, isScheduled: boolean = false, scheduledStartDate?: string, frequency?: string, paymentType?: string, firstName?: string, lastName?: string, email?: string, metadata?: any, retryConfig?: RetryConfig) => {
    interface PaymentPayload {
      paymentIdentityId: string;
      paymentType: string;
      amount: number;
      status: string;
      paymentAggregatorResponse: {
        referenceId: string;
        resultCode: number;
        resultText: string;
        customerId: string;
        methodReferenceId: string;
      };
      paymentReferenceId: string;
      responseData: any;
      scheduledStartDate?: string;
      frequency?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      metadata?: any;
    }
    
    // Prepare the request payload
    const payload: PaymentPayload = {
      paymentIdentityId: paymentIdentityId || '',
      paymentType: paymentType || (isScheduled ? "RECURRING" : "ONE_TIME"),
      amount: amount,
      status: payabaliResponse.isSuccess ? "SUCCESS" : "FAILED",
      paymentAggregatorResponse: {
        referenceId: payabaliResponse?.responseData?.referenceId || '',
        resultCode: payabaliResponse?.responseData?.resultCode || 1,
        resultText: payabaliResponse?.responseData?.resultText || "Added",
        customerId: payabaliResponse?.responseData?.customerId || '',
        methodReferenceId: payabaliResponse?.responseData?.methodReferenceId || payabaliResponse?.responseData?.referenceId || ''
      },
      scheduledStartDate: scheduledStartDate || new Date().toISOString().split('T')[0],
      frequency: frequency || 'ONETIME',
      paymentReferenceId: referenceId,
      responseData: payabaliResponse.responseData || {},
      firstName: firstName,
      lastName: lastName,
      email: email,
      metadata: metadata
    };
    
    // Add scheduled payment fields if this is a scheduled payment
    if (isScheduled && scheduledStartDate && frequency) {
      payload.scheduledStartDate = scheduledStartDate;
      payload.frequency = frequency.toUpperCase();
    }
    
    const response = await axiosInstance.post("/public/api/payments/process-transaction-and-update-payment", payload);
    return response.data;
  };

export const createDonationSubscription = async (data: {
  amount: number;
  frequency: string;
  startDate: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  specialDesignation?: string;
  paymentAggregatorResponse: {
    customerId: string;
    methodReferenceId: string;
  };
  metadata?: any;
}) => {
  const response = await axiosInstance.post('/public/api/payments/create-donation-subscription', data);
  return response.data;
};


  export const updatePatientPostPayment = async (amount : number = 0, referenceId: string, payabaliResponse: any, isScheduled: boolean = false, scheduledStartDate?: string, frequency?: string, guarantorLastName?: string, guarantorNumber?: string) => {
    interface PaymentPayload {
      paymentType: string;
      amount: number;
      status: string;
      paymentAggregatorResponse: {
        referenceId: string;
        resultCode: number;
        resultText: string;
        customerId: string;
        methodReferenceId: string;
      };
      paymentReferenceId: string;
      responseData: any;
      scheduledStartDate?: string;
      frequency?: string;
      metadata?: any;
      guarantorLastName?: string;
      guarantorNumber?: string;
    }
    
    // Prepare the request payload
    const payload: PaymentPayload = {
      paymentType: (isScheduled ? "RECURRING" : "ONE_TIME"),
      amount: amount,
      status: payabaliResponse.isSuccess ? "SUCCESS" : "FAILED",
      paymentAggregatorResponse: {
        referenceId: payabaliResponse?.responseData?.referenceId || '',
        resultCode: payabaliResponse?.responseData?.resultCode || 1,
        resultText: payabaliResponse?.responseData?.resultText || "Added",
        customerId: payabaliResponse?.responseData?.customerId || '',
        methodReferenceId: payabaliResponse?.responseData?.methodReferenceId || payabaliResponse?.responseData?.referenceId || ''
      },
      scheduledStartDate: scheduledStartDate || new Date().toISOString().split('T')[0],
      frequency: frequency || 'ONETIME',
      paymentReferenceId: referenceId,
      responseData: payabaliResponse.responseData || {},
      guarantorLastName: guarantorLastName,
      guarantorNumber: guarantorNumber
    };
    
    // Add scheduled payment fields if this is a scheduled payment
    if (isScheduled && scheduledStartDate && frequency) {
      payload.scheduledStartDate = scheduledStartDate;
      payload.frequency = frequency.toUpperCase();
    }
    
    const response = await axiosInstance.post("/public/api/payments/process-patient-transaction-and-update-payment", payload);
    return response.data;
  };
  
  export const AddNewUserSessionId = async (username: string, sessionId: string) => {
    try {
      const response = await axiosInstance.post("/api/sessions", {
        userId: localStorage.getItem("userid"),
        sessionId: sessionId,
      });
      return response.data;
    } catch (error) {
      console.error("Error in Add New User Session API:", error);
      throw error;
    }
  };
  
  export const changePasswordApi = async (oldPassword: string, newPassword: string) => {
    try {
      const response = await axiosInstance.post("/auth/changepassword", {
        accessToken: getCookie("Authorized"),
        username: localStorage.getItem("username"),
        oldPassword: oldPassword,
        newPassword: newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Error in Change Password API:", error);
      return null;
    }
  };
  
  export const sendInsuranceData = async (formData: any) => {
    try {
      const response = await axiosInstance.post("/auth/ovum/user", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        membershipId: formData.insuranceMemberId,
        insuranceCompanyName: formData.insuranceCompany,
        insuranceExist: !!formData.isInsuranceExist,
        userDob: formData.dob && formatDateToYYYYMMDD(formData.dob),
        state: formData.state || "",
      });
  
      localStorageUpdateAfterLogin(response?.data);
      return response;
    } catch (error) {
      console.error("Error in sendInsuranceData API:", error);
      throw error;
    }
  };
  
  export const getMembershipEligibilityData = async (state: string, provider: string, dob: string, membership: string) => {
    try {
      const response = await axiosInstance.post("/membership_eligibility/validate", {
        providerNPIID: "1205536703",
        providerLastName: "ovum",
        location: "",
        state,
        insuranceProvider: provider,
        dob,
        membershipId: membership
      });
  
      // Add some logic for processing eligibility data if needed
      return response;
    } catch (error) {
      console.error("Error in getMembershipEligibilityData API:", error);
      return null;
    }
  };
  
  export const fetchBillsByStatus = async (status?: string) => {
    try {
      const url = status ? `/api/receipts?status=${status}` : `/api/receipts`;
      const response = await axiosInstance.get(url);
  
      const receiptsByProvider = response.data?.receiptsByProvider || [];
      const receiptsByProviderWithType = receiptsByProvider.map((receipt: any) => ({
        ...receipt,
        type: "Provider",
        statementDate: convertDateFormat(receipt.statementDate),
        dueDateUnformatted: receipt.dueDate,
        dueDate: convertDateFormat(receipt.dueDate),
        paymentDate: status ? convertDateFormat(receipt.updatedAt) : undefined,
        isPaid: !!status,
      }));
  
      return { receiptsByProvider: receiptsByProviderWithType };
    } catch (error) {
      console.error("Error fetching bills:", error);
      throw error;
    }
  };
  
  export const getPayabliTokens = async (providerId: string) => {
    try {
      const response = await axiosInstance.get(`/public/api/payments/get-donation-token`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Payabli tokens:", error);
      throw error;
    }
  };
  
  export const postPaymentForLoginPayabli = async (
    paymentId: string,
    status: string,
    receiptId: string
  ) => {
    try {
      const response = await axiosInstance.post("/api/post-payment", {
        paymentAggregator: "payabli",
        paymentIdByAggregator: paymentId,
        receiptType: "provider",
        status: status,
        receiptId: receiptId,
      });
  
      return response;
    } catch (error) {
      console.error("Error posting payment for Payabli login:", error);
      return null;
    }
  };

  export const loggerAPI = async (data: any) => {
    try {
      await axios.post(getDomain() +"/public/logger", data);
    } catch (error) {
      // If this API fails, we can log the error to the console, but we don't want to throw an error
      console.error("Error in loggerAPI:", error);
    }
  }

// Payabli subscription APIs — patient-scoped path (/api/patient/subscriptions) so a logged-in patient
// can only view/act on their OWN subscriptions. The backend scopes every {id} action to the caller
// (by user id, then verified guarantor); staff use the separate /api/provider/stluke/subscriptions.
export const fetchSubscriptionsApi = async () => {
  try {
    const response = await axiosInstance.get('/api/patient/subscriptions');
    return response.data;
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    throw error;
  }
};

export const pauseSubscriptionApi = async (subscriptionId: string) => {
  try {
    const response = await axiosInstance.post(`/api/patient/subscriptions/${subscriptionId}/pause`);
    return response.data;
  } catch (error) {
    console.error("Error pausing subscription:", error);
    throw error;
  }
};

export const resumeSubscriptionApi = async (subscriptionId: string) => {
  try {
    const response = await axiosInstance.post(`/api/patient/subscriptions/${subscriptionId}/resume`);
    return response.data;
  } catch (error) {
    console.error("Error resuming subscription:", error);
    throw error;
  }
};

export const cancelSubscriptionApi = async (subscriptionId: string) => {
  try {
    const response = await axiosInstance.delete(`/api/patient/subscriptions/${subscriptionId}`);
    return response.data;
  } catch (error) {
    console.error("Error cancelling subscription:", error);
    throw error;
  }
};

// Custom-dates recurring schedule APIs (System B — /api/patient/recurring-schedules).
// Authenticated patient endpoints (standard JWT chain). The backend binds the guarantor to the
// logged-in user, so any guarantor sent here is ignored server-side. The card must already be
// VAULTED (payabliExec("method")) — pass the vault token as storedMethodId + the Payabli response
// (which carries customerId) as paymentAggregatorResponse.
export const createPatientRecurringScheduleApi = async (payload: {
  guarantorNumber?: string;
  guarantorLastName?: string;
  amount: number;
  frequency: string;                // MONTHLY | WEEKLY | QUARTERLY | ANNUALLY
  scheduledStartDate: string;       // yyyy-MM-dd
  scheduledEndDate?: string;        // explicit end; wins over numberOfPayments if both sent
  numberOfPayments?: number;        // installment count — backend derives the exact end date for N charges
  storedMethodId?: string;          // vault referenceId (the tokenized card)
  paymentAggregatorResponse?: any;  // Payabli vault response — carries customerId
  metadata?: any;
}) => {
  try {
    const response = await axiosInstance.post('/api/patient/recurring-schedules', payload);
    // Backend wraps in ApiResponse{data,...}; unwrap to the schedule (tolerant of the old raw shape).
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error creating recurring schedule:", error);
    throw error;
  }
};

export const fetchPatientRecurringSchedulesApi = async () => {
  try {
    const response = await axiosInstance.get('/api/patient/recurring-schedules');
    // ApiResponse{data:[...]}; unwrap to the array (tolerant of the old raw-array shape).
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching recurring schedules:", error);
    throw error;
  }
};

export const pausePatientRecurringScheduleApi = async (scheduleId: string | number) => {
  try {
    const response = await axiosInstance.post(`/api/patient/recurring-schedules/${scheduleId}/pause`);
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error pausing recurring schedule:", error);
    throw error;
  }
};

export const resumePatientRecurringScheduleApi = async (scheduleId: string | number) => {
  try {
    const response = await axiosInstance.post(`/api/patient/recurring-schedules/${scheduleId}/resume`);
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error resuming recurring schedule:", error);
    throw error;
  }
};

export const cancelPatientRecurringScheduleApi = async (scheduleId: string | number) => {
  try {
    const response = await axiosInstance.delete(`/api/patient/recurring-schedules/${scheduleId}`);
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error cancelling recurring schedule:", error);
    throw error;
  }
};

export const getPaymentHistoryApi = async (page: number = 0, size: number = 20) => {
  try {
    const response = await axiosInstance.get(`/api/v2/get-payment-history?page=${page}&size=${size}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching payment history:", error);
    throw error;
  }
};

/** Response from POST /patients/status (Axis scheduling). */
export interface PatientStatusCheckApiResponse {
  existingPatient: boolean;
  athenaPatientId?: string | null;
  /** New vs existing patient for scheduling (appointment history), not Athena chart status. */
  status?: string;
  /** Athena chart account status when a match is returned (e.g. active, inactive). */
  athenaAccountStatus?: string | null;
  upcomingAppointments?: boolean;
  spravatoCustomFieldValue?: string | null;
  lastAppointment?: string | null;
}

export const checkPatientStatus = async (patientData: {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phone?: string;
  email?: string;
}): Promise<PatientStatusCheckApiResponse> => {
  try {
    const response = await axiosInstance.post<PatientStatusCheckApiResponse>('/patients/status', patientData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking patient status:', error);
    throw error;
  }
};

/** Body sent to POST /api/appointments/athena/open-slots. No override field: the primary-provider
 * hard rule (see getPrimaryProviderRestrictionFlag) has no patient-facing exception. */
export interface OpenSlotsRequestBody {
  reasonType: 'existing' | 'telehealth' | 'new';
  reason: string;
  startDate: string;
  endDate?: string;
  zipCode?: string;
  radiusMiles?: number;
  providerName?: string;
  athenaPatientId?: string;
  rescheduleFromAppointmentId?: string;
}

/** Pure builder for the open-slots request body — kept separate from the network call so it's
 * unit-testable without mocking axios. Booking passes athenaPatientId; reschedule passes
 * rescheduleFromAppointmentId (the appointment being rescheduled); neither is required together. */
export const buildOpenSlotsRequest = (params: {
  patientStatus: 'existing' | 'new' | null;
  appointmentType: 'telehealth' | 'inperson' | 'spravato';
  zip?: string;
  radiusMiles?: number;
  startDate: string;
  athenaPatientId?: string | null;
  rescheduleFromAppointmentId?: string | null;
}): OpenSlotsRequestBody => {
  const requestData: OpenSlotsRequestBody = {
    reasonType: params.patientStatus === 'existing' ? 'existing' : 'new',
    reason: params.appointmentType === 'spravato' ? 'Spravato' : (params.appointmentType === 'telehealth' ? 'Telehealth' : 'In-Person'),
    startDate: params.startDate,
  };

  // The 60-day Spravato window only applied to the booking path before this change (the
  // reschedule query never set it) — preserved here by gating on the absence of a reschedule
  // anchor, so reschedule behavior for Spravato is unchanged.
  if (params.appointmentType === 'spravato' && !params.rescheduleFromAppointmentId) {
    const endDate = new Date(params.startDate);
    endDate.setDate(endDate.getDate() + 60);
    requestData.endDate = endDate.toISOString().split('T')[0];
  }

  if ((params.appointmentType === 'inperson' || params.appointmentType === 'spravato') && params.zip) {
    requestData.zipCode = params.zip;
    requestData.radiusMiles = params.radiusMiles;
  }

  // Attached whenever known, independent of reasonType: the server's primary-provider
  // restriction keys off whether Athena actually knows this patient (athenaPatientId present),
  // not off reasonType — reasonType/patientStatus can read "new" for a patient Athena already
  // has on file (e.g. no kept appointment yet), so gating this on reasonType used to silently
  // drop the identifier the restriction needed.
  if (params.athenaPatientId) {
    requestData.athenaPatientId = params.athenaPatientId;
  }
  if (params.rescheduleFromAppointmentId) requestData.rescheduleFromAppointmentId = params.rescheduleFromAppointmentId;

  return requestData;
};

/** What the server actually decided, not a client-side guess — read off the X-Restriction-Applied
 * response header rather than re-derived from patientStatus/appointmentType, since the backend has
 * several fail-open outcomes (no primary provider, resolution failed, no patient id) where the
 * restriction never applies even though the request looked like a candidate for it.
 *
 * `providers` stays `any[]`: the real shape is one of three near-identical, not-quite-compatible
 * local `Provider`/`ProviderGroup` interfaces (AxisSchedulingRouter, ProviderSelectionStep,
 * ConfirmationStep, RescheduleExistingStep all define their own, differing in which fields are
 * optional). Introducing a shared type here without unifying those first just trades one type gap
 * for real assignability errors across four files — not a fix worth making on its own. */
export interface OpenSlotsResult {
  providers: any[];
  restrictionApplied: boolean;
}

const readRestrictionApplied = (response: { headers: Record<string, any> }): boolean =>
  response.headers['x-restriction-applied'] === 'true';

// Get open appointment slots from Athena
export const getOpenSlots = async (requestData: OpenSlotsRequestBody): Promise<OpenSlotsResult> => {
  try {
    const response = await axiosInstance.post('/api/appointments/athena/open-slots', requestData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return { providers: response.data, restrictionApplied: readRestrictionApplied(response) };
  } catch (error) {
    console.error('Error fetching open slots:', error);
    throw error;
  }
};

// Book appointment with Athena
export const bookAppointment = async (appointmentData: {
  appointmentId: string;
  departmentId: string;
  providerId: string;
  patientId?: string;
  appointmentTypeId: string;
  reason: string;
  reasonType: 'existing' | 'new';
  insuranceCompany: string;
  insuranceIdNumber: string;
  insuranceNote?: string;
  referralSource?: string;
  // Appointment date and time
  appointmentDate?: string;
  appointmentTime?: string;
  // For new patients
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  sex?: string;
  phone?: string;
  phoneType?: 'mobile' | 'home';
  email?: string;
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
}) => {
  try {
    const payload = { ...appointmentData } as any;

    // normalizeDob not used; keeping here if needed in future

    if (payload.dateOfBirth && typeof payload.dateOfBirth === 'string') {
      const raw = payload.dateOfBirth.trim();
      // If user provided MM/DD/YYYY, pass it through unchanged
      if (raw.includes('/')) {
        payload.dateOfBirth = raw;
      } else {
        // Otherwise, leave as-is and allow backend to validate; do not over-convert
        payload.dateOfBirth = raw;
      }
    }

    const response = await axiosInstance.post('/api/appointments/athena/book', payload, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error booking appointment:', error);
    throw error;
  }
};

export const bookBulkAppointments = async (appointmentData: {
  patientId: string;
  reasonType: 'existing' | 'new';
  appointmentTypeId: string;
  reason: string;
  insuranceCompany?: string;
  insuranceIdNumber?: string;
  insuranceNote?: string;
  referralSource?: string;
  reasonForVisit?: string;
  items: Array<{
    appointmentId: string;
    departmentId: string;
    providerId: string;
    appointmentDate: string;
    appointmentTime: string;
  }>;
}) => {
  try {
    const response = await axiosInstance.post('/api/appointments/athena/book/bulk', appointmentData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error booking bulk appointments:', error);
    throw error;
  }
};

// Cancel appointment with Athena
export const cancelAppointment = async (appointmentData: {
  appointmentId: string;
  departmentId?: string;
  providerId?: string;
  patientId: string; // Make patientId required
  reason: string;
}) => {
  try {
    const response = await axiosInstance.put('/api/appointments/athena/cancel', appointmentData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error canceling appointment:', error);
    throw error;
  }
};

// Reschedule appointment with Athena
export const rescheduleAppointment = async (appointmentData: {
  appointmentId: string;
  targetAppointmentId: string;
  departmentId?: string;
  providerId?: string;
  patientId?: string;
  bookingNote?: string;
}) => {
  try {
    const response = await axiosInstance.put('/api/appointments/athena/reschedule', appointmentData, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error rescheduling appointment:', error);
    throw error;
  }
};

// Get existing appointments for a patient
export const getPatientAppointments = async (patientId: string, status?: string) => {
  try {
    const queryParams = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await axiosInstance.get(`/api/appointments/athena/patients/${patientId}/appointments${queryParams}`, {
      headers: {
        'Domain': 'axis',
        'banner': 'axis',
        'client': 'axis'
      }
    });
    // Return the appointments array from the response
    return response.data.appointments || [];
  } catch (error) {
    console.error('Error fetching patient appointments:', error);
    throw error;
  }
};

//Get provider-specific open slots by provider ID or scheduling name (auto-detects)
export const getProviderSpecificOpenSlots = async (
  providerIdentifier: string,
  params: {
    // Direct pattern parameters (recommended for rescheduling)
    departmentId?: string;
    appointmentTypeId?: string;
    patientId?: string;
    // AxisSchedulingRouter passes buildOpenSlotsRequest's output straight through, which names
    // this field athenaPatientId rather than patientId — accepted as a fallback below so that
    // identifier actually reaches the query string instead of silently being dropped.
    athenaPatientId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    // Legacy reason-based pattern parameters
    reasonType?: string;
    reason?: string;
    zipCode?: string;
    radiusMiles?: number;
    // Primary-provider hard rule: resolved server-side, no override.
    rescheduleFromAppointmentId?: string;
  }
): Promise<OpenSlotsResult> => {
  try {
    const isNumericId = /^\d+$/.test(providerIdentifier);
    
    console.log('🔍 getProviderSpecificOpenSlots called:', {
      providerIdentifier,
      isNumericId,
      params
    });
    
    // Use direct pattern if departmentId and appointmentTypeId are provided
    const useDirectPattern = params.departmentId && params.appointmentTypeId;
    const patientId = params.patientId ?? params.athenaPatientId;

    const queryParams = new URLSearchParams();

    if (useDirectPattern) {
      // Direct Athena API Call pattern (Pattern 1)
      queryParams.append('departmentId', params.departmentId!);
      queryParams.append('appointmentTypeId', params.appointmentTypeId!);
      if (patientId) queryParams.append('patientId', patientId);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.limit) queryParams.append('limit', String(params.limit));
      if (params.offset) queryParams.append('offset', String(params.offset));
      if (params.rescheduleFromAppointmentId) queryParams.append('rescheduleFromAppointmentId', params.rescheduleFromAppointmentId);
    } else {
      // Reason-based mapping pattern (Pattern 2 - legacy)
      if (patientId) queryParams.append('patientId', patientId);
      if (params.reasonType) queryParams.append('reasonType', params.reasonType);
      if (params.reason) queryParams.append('reason', params.reason);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.zipCode) queryParams.append('zipCode', params.zipCode);
      if (params.radiusMiles) queryParams.append('radiusMiles', String(params.radiusMiles));
      if (params.limit) queryParams.append('limit', String(params.limit));
      if (params.offset) queryParams.append('offset', String(params.offset));
      if (params.rescheduleFromAppointmentId) queryParams.append('rescheduleFromAppointmentId', params.rescheduleFromAppointmentId);
    }

    const url = isNumericId
      ? `/api/appointments/athena/providers/${providerIdentifier}/open-slots?${queryParams}`
      : `/api/appointments/athena/providers/scheduling-name/${encodeURIComponent(providerIdentifier)}/open-slots?${queryParams}`;

    console.log('📞 Making provider-specific API call to:', url);

    const response = await axiosInstance.get(url, {
      headers: {
        Domain: 'axis',
        banner: 'axis',
        client: 'axis',
      },
    });
    
    console.log('✅ Provider-specific API response:', {
      providerIdentifier,
      responseLength: response.data?.length || 0,
      providers: response.data?.map((p: any) => ({ 
        id: p.providerId, 
        name: `${p.firstName} ${p.lastName}`,
        schedulingName: p.schedulingName 
      })) || []
    });

    return { providers: response.data, restrictionApplied: readRestrictionApplied(response) };
  } catch (error) {
    console.error('❌ Error fetching provider-specific open slots:', error);
    throw error;
  }
};

// Fetch providers filtered by appointment type and zip code
export const getProviders = async (appointmentType: 'telehealth' | 'inperson', zip?: string) => {
  try {
    const params: any = {
      type: appointmentType,
    };

    // Only include zip in params if it's provided and not empty
    if (zip && zip.trim() !== '') {
      params.zip = zip;
    }

    const response = await axiosInstance.get('/providers', {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching providers:', error);
    throw error;
  }
};
